export default function invoicePage(config) {
    return {
        selected: [],
        sendableIds: config.sendableIds ?? [],
        filteredSendableIds: [],
        counts: {},
        errorMessage: null,
        isSending: false,
        simulateError: false,
        preview: {open: false, loading: false, title: '', subtitle: '', details: [], html: ''},
        progressSamples: [],
        finishedTask: null,
        lastTask: config.lastTask ?? null,

        init() {

            if (config.activeTask) {
                Alpine.store('tray').addTask(config.activeTask);
            }

            window.addEventListener('background-task-finished', (event) => {
                if (event.detail.task.subject_id === config.jobId) {
                    this.showFinished(event.detail.task, event.detail.notification);
                }
            });

            window.addEventListener('background-task-progress', (event) => {
                if (event.detail.task.subject_id === config.jobId) {
                    this.progressSamples.push({at: Date.now(), done: event.detail.task.done});
                    this.progressSamples = this.progressSamples.slice(-12);
                    window.dispatchEvent(new CustomEvent('refresh-table'));
                }
            });

            window.addEventListener('invoice-dev-reset', () => this.devReset());
        },

        get activeTask() {
            return Alpine.store('tray').taskForSubject(config.jobId);
        },

        get shownTask() {
            return this.activeTask ?? this.finishedTask;
        },

        get lastSendText() {
            if (!this.lastTask) return '';

            return 'Senaste sändning ' + this.lastTask.finished_at_human
                + (this.lastTask.started_by ? ' av ' + this.lastTask.started_by : '');
        },

        showFinished(task, notification) {
            window.dispatchEvent(new CustomEvent('refresh-table'));

            if (!notification) {
                window.location.reload();
                return;
            }

            const result = notification.extra ?? {};
            const finishedAt = new Date().toLocaleString('sv-SE', {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});

            this.finishedTask = {...task, done: result.done ?? task.total, failed: result.failed ?? 0, percent: 100};

            setTimeout(() => {
                this.lastTask = {
                    ...this.finishedTask,
                    finished_at_human: finishedAt.replace('.', ''),
                    can_retry: result.status === 'failed',
                    retry_url: notification.action?.url ?? null,
                };
                this.finishedTask = null;
            }, 2500);
        },

        get cardState() {
            return this.shownTask ? 'sending' : this.restingState;
        },

        get restingState() {
            if (this.lastTask?.can_retry) return 'aborted';
            if ((this.counts.failed ?? 0) > 0) return 'failed';
            if ((this.counts.unsent ?? 0) > 0) return 'unsent';
            if ((this.counts.waiting ?? 0) > 0) return 'waiting';
            if ((this.counts.sent ?? 0) > 0) return 'done';
            return 'empty';
        },

        get sendingNotice() {
            if (this.activeTask?.heartbeat_lost) return 'lost';
            if (this.activeTask?.status === 'stalled') return 'stalled';
            return 'ok';
        },

        get onlyFailuresLeft() {
            return (this.counts.unsent ?? 0) === 0 && (this.counts.failed ?? 0) > 0;
        },

        get allFilteredSelected() {
            return this.filteredSendableIds.length > 0
                && this.filteredSendableIds.every(id => this.selected.includes(id));
        },

        formatNumber(value) {
            return (value ?? 0).toLocaleString('sv-SE');
        },

        get etaText() {
            const task = this.activeTask;
            const samples = this.progressSamples;
            if (!task || samples.length < 2) return '';

            const first = samples[0];
            const last = samples[samples.length - 1];
            const perMs = (last.done - first.done) / Math.max(1, last.at - first.at);
            if (perMs <= 0) return '';

            const minutes = Math.ceil((task.total - task.done) / perMs / 60000);
            return minutes <= 1 ? 'strax klart' : `ca ${minutes} min kvar`;
        },

        get selectedCount() {
            return this.selected.length;
        },

        isSelected(id) {
            return this.selected.includes(id);
        },

        toggle(id) {
            const index = this.selected.indexOf(id);
            if (index === -1) {
                this.selected.push(id);
            } else {
                this.selected.splice(index, 1);
            }
        },

        selectAllSendable() {
            this.selected = [...this.filteredSendableIds];
        },

        clearSelection() {
            this.selected = [];
        },

        toggleSelectAll() {
            if (this.allFilteredSelected) {
                this.clearSelection();
            } else {
                this.selectAllSendable();
            }
        },

        onTableLoaded(meta) {
            this.counts = meta.counts ?? this.counts;
            this.filteredSendableIds = meta.sendable_ids ?? [];
        },

        async sendInvoices() {
            if (this.selected.length === 0 || this.isSending) {
                return;
            }

            this.isSending = true;
            this.errorMessage = null;

            try {
                const response = await fetch(config.sendUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.head.querySelector('meta[name=csrf-token]').content,
                    },
                    body: JSON.stringify({invoices: this.selected, simulate_error: this.simulateError}),
                });

                const body = await response.json().catch(() => ({}));

                if (!response.ok) {
                    this.errorMessage = body.message ?? 'Något gick fel. Inga fakturor skickades.';
                    return;
                }

                Alpine.store('tray').addTask(body.data);
                this.clearSelection();
                this.progressSamples = [];
                window.dispatchEvent(new CustomEvent('refresh-table'));
            } catch (error) {
                console.error('Error:', error);
                this.errorMessage = 'Något gick fel. Inga fakturor skickades.';
            } finally {
                this.isSending = false;
            }
        },

        async showInvoice(dataset) {
            this.preview = {
                open: true,
                loading: true,
                title: dataset.name || dataset.member || '',
                subtitle: dataset.invoiceNumber ? `Fakturanummer ${dataset.invoiceNumber}` : 'Ej skickad',
                details: [
                    dataset.invoiceDate ? `Fakturadatum ${dataset.invoiceDate}` : null,
                    dataset.dueDate ? `Förfallodatum ${dataset.dueDate}` : null,
                    dataset.sentAt ? `Skickad ${dataset.sentAt}` : null,
                ].filter(Boolean),
                html: '',
            };

            try {
                const response = await fetch(`${config.previewUrl}/${dataset.invoiceId}`);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                this.preview.html = await response.text();
            } catch (error) {
                console.error('Error:', error);
                this.preview.html = '<p class="text-sm text-red-700">Kunde inte hämta fakturan.</p>';
            } finally {
                this.preview.loading = false;
            }
        },

        closePreview() {
            this.preview.open = false;
        },

        async recheckActive() {
            if (!this.activeTask?.recheck_url) return;

            this.errorMessage = await Alpine.store('tray').restart(this.activeTask.recheck_url);
        },

        async retryLastRun() {
            if (!this.lastTask?.retry_url) return;

            const message = await Alpine.store('tray').restart(this.lastTask.retry_url);
            if (message) {
                this.errorMessage = message;
                return;
            }

            window.location.reload();
        },

        async devReset() {
            if (!config.devResetUrl) return;

            const response = await fetch(config.devResetUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': document.head.querySelector('meta[name=csrf-token]').content,
                },
            });

            if (response.ok) {
                window.location.reload();
            } else {
                this.errorMessage = 'Kunde inte återställa jobbet.';
            }
        },
    };
}
